/**
 * Reusable pulse skeletons for loading states (prefer over spinners for layout/content).
 */

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700 ${className}`} aria-hidden />
}

/** Guest list modal body while bookings/stats load */
export function GuestListManageSkeleton() {
  return (
    <div className="min-w-0 space-y-6">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-3 w-full max-w-lg" />
        <div className="flex flex-wrap gap-2 items-center">
          <Skeleton className="h-9 w-44" />
          <Skeleton className="h-9 flex-1 min-w-[160px] max-w-md" />
          <Skeleton className="h-9 w-[72px]" />
        </div>
        <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 md:p-5 space-y-2"
          >
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-14" />
          </div>
        ))}
      </div>
      <Skeleton className="h-12 w-full max-w-xl rounded-lg" />
    </div>
  )
}

/** Links page table area (parent supplies outer card border) */
export function LinksTableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex gap-4 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-white/[0.02]">
        <Skeleton className="h-3 w-[18%]" />
        <Skeleton className="h-3 w-[14%]" />
        <Skeleton className="h-3 w-[16%]" />
        <Skeleton className="h-3 w-[10%]" />
        <Skeleton className="h-3 w-[10%] ml-auto" />
        <Skeleton className="h-3 w-[12%]" />
        <Skeleton className="h-3 w-[20%] ml-auto" />
      </div>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`flex items-center gap-4 px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
            i % 2 === 1 ? 'bg-gray-50/40 dark:bg-white/[0.02]' : ''
          }`}
        >
          <Skeleton className="h-4 w-[18%]" />
          <Skeleton className="h-4 w-[14%]" />
          <Skeleton className="h-4 w-[16%]" />
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-4 w-8 ml-auto" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
      ))}
    </div>
  )
}

const MUSIC_CATALOG_SKELETON_ROW_COUNT = 6

/**
 * Music page catalog: search/filter bar + card rows (mobile) + table (lg+), aligned with the real list layout.
 */
export function MusicCatalogLineItemsSkeleton({
  showAccountColumn = false,
  showStreamingColumns = false,
}: {
  showAccountColumn?: boolean
  showStreamingColumns?: boolean
}) {
  return (
    <div className="space-y-4" role="status" aria-label="Loading catalog">
      <div className="mb-4 space-y-3">
        <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3">
          <Skeleton className="h-10 w-full max-w-md rounded-lg" />
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Skeleton className="h-9 w-[72px] rounded-md" />
            <Skeleton className="h-9 w-[120px] rounded-md" />
            {showAccountColumn && <Skeleton className="h-9 w-[140px] rounded-md" />}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <div className="lg:hidden space-y-3 mb-6">
        {Array.from({ length: MUSIC_CATALOG_SKELETON_ROW_COUNT }).map((_, i) => (
          <div
            key={i}
            className={`rounded-lg border p-4 border-gray-200 dark:border-gray-700 ${
              i % 2 === 0
                ? 'bg-white dark:bg-[rgb(var(--surface-rgb))]'
                : 'bg-gray-50/50 dark:bg-[rgb(var(--surface-2-rgb))]'
            }`}
          >
            <div className="flex items-start gap-3">
              <Skeleton className="h-12 w-12 rounded-md flex-shrink-0" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4 max-w-xs" />
                <Skeleton className="h-3 w-1/2 max-w-[200px]" />
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Skeleton className="h-6 w-24 rounded-md" />
                  <Skeleton className="h-2.5 w-2.5 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden lg:block overflow-x-auto mb-6">
        <div className="bg-white dark:bg-[rgb(var(--surface-rgb))] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-w-[920px]">
          <table className="w-full border-collapse table-fixed min-w-[920px]">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-white/[0.02]">
                <th className="py-3 px-4 w-16" />
                <th className="text-left py-3 px-4">
                  <Skeleton className="h-3 w-9" />
                </th>
                <th className="text-left py-3 px-4">
                  <Skeleton className="h-3 w-8" />
                </th>
                <th className="text-left py-3 px-4">
                  <Skeleton className="h-3 w-9" />
                </th>
                {showAccountColumn && (
                  <th className="text-left py-3 px-4">
                    <Skeleton className="h-3 w-14" />
                  </th>
                )}
                <th className="text-left py-3 px-4">
                  <Skeleton className="h-3 w-10" />
                </th>
                {showStreamingColumns && (
                  <>
                    <th className="text-right py-3 px-4">
                      <Skeleton className="h-3 w-20 ml-auto" />
                    </th>
                    <th className="text-right py-3 px-4">
                      <Skeleton className="h-3 w-9 ml-auto" />
                    </th>
                    <th className="text-right py-3 px-4">
                      <Skeleton className="h-3 w-16 ml-auto" />
                    </th>
                  </>
                )}
                <th className="w-11 py-3 px-1" />
                <th className="py-3 px-4 w-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {Array.from({ length: MUSIC_CATALOG_SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className={i % 2 === 1 ? 'bg-gray-50/60 dark:bg-white/[0.02]' : ''}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-4 rounded" />
                      <Skeleton className="h-2.5 w-2.5 rounded-full" />
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <Skeleton className="h-9 w-9 rounded-md flex-shrink-0" />
                      <Skeleton className="h-4 w-[min(12rem,90%)]" />
                    </div>
                  </td>
                  <td className="py-3 px-4 align-middle">
                    <Skeleton className="h-4 w-20" />
                  </td>
                  <td className="py-3 px-4 align-middle">
                    <Skeleton className="h-4 w-32" />
                  </td>
                  {showAccountColumn && (
                    <td className="py-3 px-4 align-middle">
                      <Skeleton className="h-4 w-24" />
                    </td>
                  )}
                  <td className="py-3 px-4 align-middle">
                    <Skeleton className="h-8 w-[5.5rem] rounded-md" />
                  </td>
                  {showStreamingColumns && (
                    <>
                      <td className="py-3 px-4 text-right align-middle">
                        <Skeleton className="h-4 w-12 ml-auto" />
                      </td>
                      <td className="py-3 px-4 text-right align-middle">
                        <Skeleton className="h-4 w-6 ml-auto" />
                      </td>
                      <td className="py-3 px-4 text-right align-middle">
                        <Skeleton className="h-4 w-10 ml-auto" />
                      </td>
                    </>
                  )}
                  <td className="w-11 py-3 px-1 text-right align-middle">
                    <Skeleton className="h-7 w-7 rounded-md ml-auto" />
                  </td>
                  <td className="py-3 px-4" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/** Public guest list page (dark) initial load */
export function GuestListPublicSkeleton() {
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 animate-pulse">
      <Skeleton className="h-8 w-48 mb-8 rounded-lg bg-gray-800" />
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="h-14 w-full rounded-xl bg-gray-800" />
        <Skeleton className="h-14 w-full rounded-xl bg-gray-800" />
        <Skeleton className="h-14 w-full rounded-xl bg-gray-800" />
      </div>
      <Skeleton className="h-12 w-full max-w-md mt-10 rounded-xl bg-gray-800" />
    </div>
  )
}

/** Deals list card */
export function DealsListSkeleton() {
  return (
    <div className="bg-white dark:bg-[rgb(var(--surface-rgb))] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden animate-pulse">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex gap-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-20 ml-auto" />
      </div>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="p-4 flex items-center gap-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
          <Skeleton className="h-4 flex-1 max-w-xs" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  )
}

/** Deal detail page */
export function DealDetailSkeleton() {
  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto animate-pulse space-y-6">
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-8 w-40 rounded-lg" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
      <Skeleton className="h-10 w-full max-w-2xl rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-28" />
          </div>
        ))}
      </div>
      <Skeleton className="h-40 w-full rounded-lg" />
    </div>
  )
}

/** Team member edit header + form */
export function TeamMemberEditSkeleton() {
  return (
    <div className="p-4 pt-16 md:pt-8 md:p-8 max-w-6xl mx-auto animate-pulse space-y-8">
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <div className="space-y-4 max-w-xl">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    </div>
  )
}

/** Team management panel (Manage Team tab / artist team page) while members load */
export function TeamManagePanelSkeleton({ variant = 'single' }: { variant?: 'single' | 'multi' }) {
  const card = (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-56 max-w-full" />
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 px-6 py-4">
            <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0 space-y-2">
              <Skeleton className="h-4 w-48 max-w-[min(100%,12rem)]" />
              <Skeleton className="h-3 w-32 max-w-full" />
            </div>
            <Skeleton className="h-6 w-14 rounded-full flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  )
  if (variant === 'multi') {
    return (
      <div className="space-y-6 mb-10 animate-pulse" aria-hidden>
        {card}
        {card}
      </div>
    )
  }
  return (
    <div className="animate-pulse mb-10" aria-hidden>
      {card}
    </div>
  )
}

/** Team hub “My accounts” list while managed artists / invitations load */
export function TeamHubAccountsSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-hidden>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <Skeleton className="h-4 w-44" />
          <Skeleton className="h-3 w-64 max-w-full mt-2" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 last:border-0"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
              <div className="space-y-2 min-w-0">
                <Skeleton className="h-4 w-40 max-w-[min(100%,14rem)]" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
            <Skeleton className="h-8 w-[4.5rem] rounded-md flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** Sidebar chat conversation list */
export function SidebarChatHistorySkeleton() {
  return (
    <div className="px-4 py-2 space-y-2" aria-hidden>
      <Skeleton className="h-3 w-3/4 max-w-[140px]" />
      <Skeleton className="h-3 w-2/3 max-w-[120px]" />
      <Skeleton className="h-3 w-4/5 max-w-[150px]" />
    </div>
  )
}

/** Narrow / mobile shows empty state area */
export function ShowsMobileSkeleton() {
  return (
    <div className="flex flex-col items-stretch gap-3 p-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-4 w-40 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
  )
}

/** Long-running import (Spotify / Bandsintown) — no spinner */
export function CatalogImportSkeleton({
  message,
  subtitle,
}: {
  message: string
  subtitle?: string
}) {
  return (
    <div className="bg-white dark:bg-[rgb(var(--surface-rgb))] rounded-lg border border-gray-200 dark:border-gray-700 p-8 md:p-12">
      <div className="max-w-md mx-auto space-y-4 animate-pulse">
        <Skeleton className="h-5 w-56 mx-auto rounded" />
        <Skeleton className="h-3 w-full max-w-sm mx-auto rounded" />
        <div className="space-y-2 pt-4">
          <Skeleton className="h-3 w-full rounded" />
          <Skeleton className="h-3 w-[90%] rounded" />
          <Skeleton className="h-3 w-[75%] rounded" />
        </div>
      </div>
      <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300 mt-6">{message}</p>
      {subtitle ? (
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>
      ) : null}
    </div>
  )
}

/** Tour manager show detail panel */
export function TourShowDetailSkeleton() {
  return (
    <div className="space-y-5">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 overflow-hidden animate-pulse"
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
            <Skeleton className="h-4 w-28" />
          </div>
          <div className="p-4 space-y-2">
            <Skeleton className="h-3 w-full max-w-md" />
            <Skeleton className="h-3 max-w-sm w-[85%]" />
            <Skeleton className="h-3 max-w-xs w-[65%]" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Admin overview / activity / system tab initial load */
export function AdminDashboardSkeleton() {
  return (
    <div className="space-y-8 py-4 animate-pulse">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-100 border border-gray-200" />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-gray-100 border border-gray-200" />
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="h-64 rounded-xl bg-gray-100 border border-gray-200" />
        <div className="h-64 rounded-xl bg-gray-100 border border-gray-200" />
      </div>
    </div>
  )
}

/** Admin shows / financials tables — block placeholder (colSpan 6) */
export function AdminTableBlockSkeleton() {
  return (
    <tr>
      <td colSpan={6} className="py-8 px-5">
        <div className="space-y-4 animate-pulse">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 items-center">
              <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2 max-w-2xl">
                <Skeleton className="h-3 w-[45%] max-w-[220px]" />
                <Skeleton className="h-3 w-[70%] max-w-[320px]" />
              </div>
            </div>
          ))}
        </div>
      </td>
    </tr>
  )
}

/** Activity tab */
export function AdminActivityTabSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <Skeleton className="h-5 w-44" />
        <Skeleton className="h-3 w-12" />
      </div>
      <div className="divide-y divide-gray-50">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="px-5 py-3 flex items-start gap-3">
            <Skeleton className="h-5 w-14 rounded-full flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2 min-w-0">
              <Skeleton className="h-3 w-full max-w-lg" />
              <Skeleton className="h-3 w-[50%] max-w-xs" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** System health tab */
export function AdminSystemTabSkeleton() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-28 rounded-xl bg-gray-100 border border-gray-200" />
      ))}
    </div>
  )
}

/** Admin users table — skeleton rows matching 6-column layout */
export function AdminUsersTableSkeletonRows() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <tr key={i} className="animate-pulse border-b border-gray-50 last:border-0">
          <td className="px-5 py-3">
            <div className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
              <div className="space-y-2 min-w-0">
                <Skeleton className="h-3.5 w-32 max-w-full" />
                <Skeleton className="h-3 w-40 max-w-full" />
              </div>
            </div>
          </td>
          <td className="px-5 py-3">
            <Skeleton className="h-5 w-14 rounded-full" />
          </td>
          <td className="px-5 py-3 hidden md:table-cell">
            <Skeleton className="h-3 w-24" />
          </td>
          <td className="px-5 py-3 hidden lg:table-cell">
            <Skeleton className="h-3 w-20" />
          </td>
          <td className="px-5 py-3 hidden lg:table-cell">
            <Skeleton className="h-3 w-20" />
          </td>
          <td className="px-5 py-3 text-right">
            <Skeleton className="h-6 w-10 ml-auto rounded" />
          </td>
        </tr>
      ))}
    </>
  )
}

/** User detail modal body */
export function AdminUserDetailSkeleton() {
  return (
    <div className="space-y-4 animate-pulse py-2">
      <div className="flex items-center gap-3">
        <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-56 max-w-full" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-2">
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
      </div>
    </div>
  )
}

/** Modal / dropdown search results list */
export function SearchResultsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex gap-3 px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0"
        >
          <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
          <div className="space-y-2 flex-1 py-0.5 min-w-0">
            <Skeleton className="h-3.5 w-40 max-w-full" />
            <Skeleton className="h-3 w-56 max-w-full" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Chat-style “typing” placeholder */
export function ChatTypingSkeleton() {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 dark:bg-[#2a2a2a] rounded-lg px-3 py-2.5 space-y-1.5 min-w-[120px]">
        <div className="h-2 w-full rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
        <div className="h-2 w-[85%] rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
        <div className="h-2 w-[60%] rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
      </div>
    </div>
  )
}
